// ============================================================
// ◕ COIN GLYPH — the Illegal Empire's currency sigils
// ------------------------------------------------------------
// One file, one source of truth for what money LOOKS like in
// Terror. Import the component; never hand-draw a coin again.
//
//   <ChocolateCoin size={16} />   ◕  Terror's money
//   <DivineCrest   size={16} />   ◆  the Empire's reserve
//   <CoinBadge amount={600} />       the HUD pill
//   <CrestBadge amount={5} />        the HUD pill
//
// WHY INLINE SVG AND NOT A CDN FILE
// caP'n uploaded chocolate-coin.svg to terror-assets, and the URL
// below is here for anyone who wants the standalone file. But the
// HUD coin is drawn inline on purpose:
//   • zero network requests — a currency icon renders on frame 1
//   • it CANNOT become a broken-image box. Per ie-cdn-assets, a
//     malformed asset URL pointed at the app domain returns
//     index.html with a 200 and you chase it for an hour. An
//     inline path has no domain to get wrong.
//   • it scales cleanly from 12px to 512px and inherits nothing
//   • it themes — the foil, the quicksilver and the chocolate are
//     props, so a Solrend-tinted coin is a one-line variant later
//
// THE COIN, described (from TERROR_CURRENCY_RESTRUCTURE_v1 §2):
//   gold coin, perfect circle, milled rim, foil torn back down one
//   side showing quicksilver underside, dark chocolate exposed
//   beneath.
// The silhouette stays a PERFECT CIRCLE at every size — that is the
// whole reason it reads at 24px in a crowded HUD. If you ever add
// to this drawing, add it INSIDE the circle. Never break the round.
// ============================================================

import { GM } from "./theme";
import { CDN } from "./CDN"; // one domain, one file (sweep 2026-08-18)

// Gradient ids must be unique per mounted instance or the first coin
// on the page wins and every later one renders with its palette.
let _uid = 0;
const nextId = () => `cg${++_uid}`;

// The text-only fallbacks. Logs, chat lines, alt text, anywhere a
// component can't go. These are the glyphs of record.
export const COIN_GLYPH  = "◕";
export const CREST_GLYPH = "◆";
export const SE_GLYPH    = "◈";

// caP'n's standalone file, if a surface ever wants the flat asset.
// Per ie-cdn-assets: assets live on terror-assets, NEVER on
// terror-card-game (the app domain serves index.html for every path).
// (Domain imports from CDN.js at the top — CRA's import/first rule
// rejects a mid-file import; caught live 2026-08-18.)
export const CHOCOLATE_COIN_URL = `${CDN}/chocolate-coin.svg`;

// ─────────────────────────────────────────────────────────────
// ◕ THE CHOCOLATE COIN
// ─────────────────────────────────────────────────────────────
export function ChocolateCoin({
  size = 16,
  foil       = "#f2c44e",   // gold leaf
  foilDeep   = "#9a6b16",   // where the leaf turns away from the light
  quicksilver= "#dfe4ec",   // the foil's underside, curling back
  chocolate  = "#3a2214",   // what's under all of it
  glow       = true,
  style,
  title = "Chocolate Coins",
}) {
  const id = nextId();

  return (
    <svg
      width={size} height={size} viewBox="0 0 64 64"
      role="img" aria-label={title}
      style={{
        // inline-block + a nudged baseline so the coin sits correctly BOTH
        // inline next to text ("◕ 600") and as a flex child in a HUD pill.
        // display:block was wrong for the first case and cost a pass.
        display: "inline-block", verticalAlign: "-0.16em",
        flexShrink: 0, overflow: "visible", ...style,
      }}
    >
      <title>{title}</title>
      <defs>
        {/* The leaf: lit from upper-left, falling away to the lower-right. */}
        <linearGradient id={`${id}-foil`} x1="18%" y1="8%" x2="82%" y2="94%">
          <stop offset="0%"   stopColor="#fff3c4" />
          <stop offset="26%"  stopColor={foil} />
          <stop offset="62%"  stopColor="#c9922a" />
          <stop offset="100%" stopColor={foilDeep} />
        </linearGradient>

        {/* Chocolate is matte — barely any gradient, that's the point.
            It has to read as NOT-metal next to the leaf. */}
        <linearGradient id={`${id}-choc`} x1="30%" y1="10%" x2="70%" y2="100%">
          <stop offset="0%"   stopColor="#4d2e1b" />
          <stop offset="100%" stopColor={chocolate} />
        </linearGradient>

        {/* The underside of the torn leaf — cold, not warm. The whole
            trick of the coin is this strip being a DIFFERENT metal
            temperature from the face. */}
        <linearGradient id={`${id}-quick`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"   stopColor="#ffffff" />
          <stop offset="45%"  stopColor={quicksilver} />
          <stop offset="100%" stopColor="#8e97a8" />
        </linearGradient>

        {/* Keeps every drawn thing inside the perfect round. Nothing
            escapes the silhouette — not the tear, not the milling. */}
        <clipPath id={`${id}-round`}>
          <circle cx="32" cy="32" r="30" />
        </clipPath>
      </defs>

      <g clipPath={`url(#${id}-round)`}>
        {/* 1 — chocolate underneath everything */}
        <circle cx="32" cy="32" r="30" fill={`url(#${id}-choc)`} />

        {/* 2 — the gold leaf, torn down the right side. The jag is
               deliberately irregular; a clean curve reads as a design
               choice, a ragged one reads as FOIL THAT SOMEONE TORE. */}
        <path
          d="M32 2 A30 30 0 0 0 32 62
             L38 54 L33 48 L41 41 L34 34 L42 27 L35 20 L41 12 L36 6 Z"
          fill={`url(#${id}-foil)`}
        />

        {/* 3 — the quicksilver underside, riding the tear line */}
        <path
          d="M36 6 L41 12 L35 20 L42 27 L34 34 L41 41 L33 48 L38 54 L32 62
             L28.5 58 L34 47 L27 40 L35 33 L28 26 L36 19 L30 11 Z"
          fill={`url(#${id}-quick)`}
          opacity="0.95"
        />

        {/* 4 — chocolate crumb shadow just past the tear, so the
               exposed side has depth instead of looking painted on */}
        <path
          d="M32 62 L28.5 58 L34 47 L27 40 L35 33 L28 26 L36 19 L30 11 L32 2
             A30 30 0 0 1 32 62 Z"
          fill="#000"
          opacity="0.28"
        />

        {/* 5 — milled rim. Ticks around the whole circumference, drawn
               as one dashed ring: cheap, even, and it survives scaling. */}
        <circle
          cx="32" cy="32" r="27.5"
          fill="none"
          stroke="rgba(0,0,0,0.42)"
          strokeWidth="5"
          strokeDasharray="2.1 3.1"
        />

        {/* 6 — the struck ring on the gold face, the "coin" tell */}
        <circle
          cx="32" cy="32" r="19"
          fill="none"
          stroke="rgba(255,240,190,0.34)"
          strokeWidth="1.4"
        />

        {/* 7 — a single specular sweep. One highlight, not three;
               more than one and it stops looking like metal. */}
        <ellipse
          cx="22" cy="18" rx="13" ry="7"
          fill="#fff8dc" opacity="0.30"
          transform="rotate(-32 22 18)"
        />
      </g>

      {/* The edge. Drawn last, on top, so nothing above bleeds past it. */}
      <circle
        cx="32" cy="32" r="30"
        fill="none"
        stroke="rgba(60,34,12,0.85)"
        strokeWidth="2"
      />
      {glow && (
        <circle
          cx="32" cy="32" r="30"
          fill="none"
          stroke={foil}
          strokeWidth="1"
          opacity="0.5"
          style={{ filter: `drop-shadow(0 0 3px ${foil})` }}
        />
      )}
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────
// ◆ THE DIVINE CREST
// ------------------------------------------------------------
// The reserve. Deliberately NOT a coin — it's a struck sigil, cold
// and cut, so a player never confuses the two at a glance in the
// same HUD row. That contrast is the point: one is spending money,
// one is the thing you hoard.
// ─────────────────────────────────────────────────────────────
export function DivineCrest({ size = 16, tint = "#eadfa0", style, title = "Divine Crests" }) {
  const id = nextId();
  return (
    <svg
      width={size} height={size} viewBox="0 0 64 64"
      role="img" aria-label={title}
      style={{
        // inline-block + a nudged baseline so the coin sits correctly BOTH
        // inline next to text ("◕ 600") and as a flex child in a HUD pill.
        // display:block was wrong for the first case and cost a pass.
        display: "inline-block", verticalAlign: "-0.16em",
        flexShrink: 0, overflow: "visible", ...style,
      }}
    >
      <title>{title}</title>
      <defs>
        <linearGradient id={`${id}-dc`} x1="20%" y1="0%" x2="80%" y2="100%">
          <stop offset="0%"   stopColor="#fffdf0" />
          <stop offset="40%"  stopColor={tint} />
          <stop offset="100%" stopColor="#8d7a2e" />
        </linearGradient>
      </defs>
      <path d="M32 3 L57 32 L32 61 L7 32 Z" fill={`url(#${id}-dc)`}
            stroke="rgba(60,50,10,0.8)" strokeWidth="2" strokeLinejoin="round" />
      <path d="M32 14 L47 32 L32 50 L17 32 Z" fill="none"
            stroke="rgba(255,255,230,0.45)" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M32 3 L57 32 L32 32 Z" fill="#fff" opacity="0.18" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────
// THE HUD PILLS
// ------------------------------------------------------------
// Same shell, two currencies, one place to restyle. Plaza used to
// hand-roll CrestBadge inline; it lives here now so the Codex,
// the Menagerie and the shop can't drift away from it.
// ─────────────────────────────────────────────────────────────
const RECESS = "linear-gradient(180deg, #14100c, #0a0806)";

function Pill({ icon, amount, label, accent, textColor, coinTarget = false }) {
  return (
    <div {...(coinTarget ? { [COIN_TARGET_ATTR]: "1" } : {})} style={{
      display: "flex", alignItems: "center", gap: 6,
      background: RECESS,
      border: `1px solid ${accent}88`,
      borderRadius: 20,
      padding: "4px 13px",
      boxShadow: `inset 0 2px 7px rgba(0,0,0,0.7), 0 0 12px ${accent}30`,
    }}>
      {icon}
      <span style={{
        fontSize: 14, fontWeight: 800, color: textColor,
        fontFamily: GM.fontUI, letterSpacing: 0.4,
        textShadow: "0 1px 2px rgba(0,0,0,0.9)",
      }}>
        {(amount ?? 0).toLocaleString()}
      </span>
      <span style={{
        fontSize: 9, color: GM.vellumFaint, letterSpacing: 2,
        fontFamily: GM.fontUI, fontWeight: 600,
      }}>{label}</span>
    </div>
  );
}

export function CoinBadge({ amount, label = "COINS", coinTarget = false }) {
  return (
    <Pill
      icon={<ChocolateCoin size={15} />}
      amount={amount}
      label={label}
      accent="#f2c44e"
      textColor="#ffe9a8"
      coinTarget={coinTarget}
    />
  );
}

export function CrestBadge({ amount, label = "CRESTS" }) {
  return (
    <Pill
      icon={<DivineCrest size={14} />}
      amount={amount}
      label={label}
      accent={GM.inkGoldBright}
      textColor="#f4ecc0"
    />
  );
}

// ─────────────────────────────────────────────────────────────
// D16 · SCRAP CRUMBLE — the crumble IS the receipt
// ------------------------------------------------------------
// When a dupe is scrapped (T21), the card doesn't toast, doesn't
// modal, doesn't apologize. It crumbles: motes break off the card,
// fall for a beat, then gather into the nearest ◕ counter, which
// pulses as they land. That animation is the entire receipt.
//
// Any coin display that wants to RECEIVE motes marks itself with
// COIN_TARGET_ATTR (CoinBadge does it via `coinTarget`, CoinChip
// always does). crumbleToCoins() finds the nearest visible target
// at fire time, so surfaces never wire refs to each other. No
// target on screen → the scrap still worked; we just skip the
// theater and call onDone. Pure DOM + WAAPI: zero React state,
// zero keyframe-injection (so the stale-sheet trap can't bite),
// safe to fire from any surface.
// ─────────────────────────────────────────────────────────────
export const COIN_TARGET_ATTR = "data-coin-target";

// Compact ◕ chip — a live balance readout that is ALSO a mote target.
// Drop it inside any scrap surface (lightbox row, reveal overlay, the
// Forge's scrap bar) so the crumble has somewhere local to gather.
export function CoinChip({ amount, size = 13, style }) {
  return (
    <span {...{ [COIN_TARGET_ATTR]: "1" }} style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: RECESS, border: "1px solid #f2c44e66",
      borderRadius: 14, padding: "2px 9px", flexShrink: 0,
      boxShadow: "inset 0 1px 5px rgba(0,0,0,0.6), 0 0 8px #f2c44e22",
      ...style,
    }}>
      <ChocolateCoin size={size} />
      <span style={{
        fontSize: 12, fontWeight: 800, color: "#ffe9a8",
        fontFamily: GM.fontUI, letterSpacing: 0.4,
        textShadow: "0 1px 2px rgba(0,0,0,0.9)",
      }}>{amount == null ? "—" : (amount ?? 0).toLocaleString()}</span>
    </span>
  );
}

// sourceEl: the DOM element of the thing being scrapped (card tile /
// lightbox art / reveal card). opts.tierColor tints ~half the motes
// for foil scraps so a Solrend melt burns crimson on the way out.
export function crumbleToCoins(sourceEl, { motes = 14, color = "#c9b48a", tierColor = null, onDone } = {}) {
  let finished = false;
  const finish = () => { if (finished) return; finished = true; try { onDone && onDone(); } catch (_) {} };
  try {
    if (!sourceEl || typeof document === "undefined" || !sourceEl.getBoundingClientRect) return finish();
    const src = sourceEl.getBoundingClientRect();
    if (!src.width || !src.height) return finish();
    const targets = Array.from(document.querySelectorAll(`[${COIN_TARGET_ATTR}]`))
      .map(el => ({ el, r: el.getBoundingClientRect() }))
      .filter(t => t.r.width > 0 && t.r.height > 0);
    if (!targets.length) return finish();
    // Nearest visible ◕ to the crumble wins the motes.
    const cx = src.left + src.width / 2, cy = src.top + src.height / 2;
    let target = targets[0], bd = Infinity;
    for (const t of targets) {
      const dx = (t.r.left + t.r.width / 2) - cx, dy = (t.r.top + t.r.height / 2) - cy;
      const d = dx * dx + dy * dy;
      if (d < bd) { bd = d; target = t; }
    }
    const tx = target.r.left + target.r.width / 2, ty = target.r.top + target.r.height / 2;

    const host = document.createElement("div");
    host.style.cssText = "position:fixed;inset:0;pointer-events:none;z-index:9999;";
    document.body.appendChild(host);

    const N = Math.max(6, Math.min(26, motes | 0));
    let landed = 0;
    const done = () => {
      landed++;
      if (landed < N) return;
      try {
        target.el.animate(
          [{ transform: "scale(1)" }, { transform: "scale(1.22)" }, { transform: "scale(1)" }],
          { duration: 300, easing: "ease-out" }
        );
      } catch (_) {}
      host.remove();
      finish();
    };

    for (let i = 0; i < N; i++) {
      const m = document.createElement("div");
      const sz = 3.5 + Math.random() * 5.5;
      const useTier = tierColor && Math.random() < 0.45;
      const tint = useTier ? tierColor : color;
      m.style.cssText =
        `position:fixed;left:0;top:0;width:${sz}px;height:${sz}px;` +
        `border-radius:${Math.random() < 0.5 ? "50%" : "2px"};background:${tint};` +
        `box-shadow:0 0 ${(sz * 2).toFixed(1)}px ${useTier ? tierColor : "#f2c44e"};` +
        `will-change:transform,opacity;`;
      host.appendChild(m);
      const mx = src.left + Math.random() * src.width;
      const my = src.top + Math.random() * src.height;
      // Phase 1: the piece breaks off and FALLS (crumble). Phase 2: it
      // remembers it's money and arcs toward the counter at FULL size
      // and full glow — the stream has to read across a long screen —
      // then SINKS into the coin only in the last fifth of the flight.
      // (Taste-tested live 2026-08-18: the original shrank all the way
      // across and the trail went faint past ~300px of travel.)
      const jx = mx + (Math.random() - 0.5) * 40;
      const jy = my + 12 + Math.random() * 30;
      const px = jx + (tx - jx) * 0.82;
      const py = jy + (ty - jy) * 0.82;
      let anim;
      try {
        anim = m.animate(
          [
            { transform: `translate(${mx}px, ${my}px) rotate(0deg) scale(1)`, opacity: 1, offset: 0 },
            { transform: `translate(${jx}px, ${jy}px) rotate(${((Math.random() - 0.5) * 150).toFixed(0)}deg) scale(0.95)`, opacity: 1, offset: 0.3, easing: "cubic-bezier(0.25,0.6,0.45,1)" },
            { transform: `translate(${px}px, ${py}px) rotate(${((Math.random() - 0.5) * 260).toFixed(0)}deg) scale(0.8)`, opacity: 1, offset: 0.78, easing: "cubic-bezier(0.5,0,0.75,0.45)" },
            { transform: `translate(${tx}px, ${ty}px) rotate(${((Math.random() - 0.5) * 320).toFixed(0)}deg) scale(0.24)`, opacity: 0.7, offset: 1, easing: "ease-in" },
          ],
          { duration: 640 + Math.random() * 420, delay: Math.random() * 180, fill: "forwards" }
        );
      } catch (_) { m.remove(); done(); continue; }
      anim.onfinish = () => { if (m.parentNode) m.remove(); done(); };
    }
    // Belt-and-suspenders: if onfinish never fires (throttled tab), the
    // theater strikes itself and the receipt is simply the number moving.
    setTimeout(() => { if (host.parentNode) { host.remove(); finish(); } }, 2600);
  } catch (_) { finish(); }
}

export default ChocolateCoin;
